import type { FriendBoardRow, FriendEntry, FriendRequestEntry, InviteResult, OnlineModeId, ServerMessage } from "@golazo/shared";
import type { OnlineClient } from "./client.js";

/**
 * Tus amigos, en vivo. El servidor manda la lista entera cuando cambia algo
 * (alguien se conecta, entra en una sala, acepta tu solicitud); aqui solo se
 * guarda la ultima y se avisa a quien la pinte.
 */
export interface FriendsState {
  code: string;
  friends: FriendEntry[];
  incoming: FriendRequestEntry[];
  outgoing: FriendRequestEntry[];
  /** A quien tienes bloqueado. */
  blocked: FriendRequestEntry[];
}

export type FriendResult = Extract<ServerMessage, { type: "friends:result" }>["result"];

export interface RoomInvite {
  roomId: string;
  roomName: string;
  modeId: OnlineModeId;
  from: FriendRequestEntry;
  humans?: number;
  capacity?: number;
  /** Cuando llego (ms): el pase del servidor dura 15 minutos. */
  at: number;
}

/** Lo que dura una invitacion en la lista: lo mismo que su pase en el servidor. */
export const INVITE_TTL_MS = 15 * 60_000;

export interface FriendsStore {
  state(): FriendsState | null;
  subscribe(fn: (state: FriendsState | null) => void): () => void;
  /** Avisos sueltos: solicitud nueva, solicitud aceptada, resultado de pedir, invitacion. */
  onEvent(fn: (event: FriendsEvent) => void): () => void;
  addByCode(code: string): void;
  addById(clientId: string): void;
  /**
   * Lo que se escriba en el campo de añadir: un apodo (son unicos) o un
   * codigo. Si puede ser las dos cosas se mandan las dos y el servidor prueba
   * primero el codigo.
   */
  addByText(text: string): void;
  block(clientId: string): void;
  unblock(clientId: string): void;
  isBlocked(clientId: string): boolean;
  respond(clientId: string, accept: boolean): void;
  remove(clientId: string): void;
  invite(roomId: string, clientId: string): void;
  /** Invitaciones que te han hecho y siguen vivas, la mas nueva primero. */
  invites(): RoomInvite[];
  /** "Ahora no": se le dice a quien invito y se quita de la lista. */
  declineInvite(roomId: string): void;
  /** Ya no hace falta (has entrado en esa sala). */
  dropInvite(roomId: string): void;
  /**
   * Has llegado por el enlace de la sala de alguien y el enlace traia su
   * codigo: si estais los dos dentro, el servidor os hace amigos sin mas.
   */
  addViaLink(code: string, roomId: string): void;
  refresh(): void;
  /** Cuantos estan conectados ahora (para el contador de la pestaña). */
  onlineCount(): number;
  isFriend(clientId: string): boolean;
  /** La ficha online de un amigo (texto del perfil), o null si no llega. */
  profileOf(clientId: string): Promise<{ nickname: string; profile: string | null } | null>;
  /** Clasificacion de la semana entre tus amigos y tu. */
  board(): Promise<FriendBoardRow[] | null>;
  dispose(): void;
}

export type FriendsEvent =
  | { kind: "request" | "accepted"; from: FriendRequestEntry }
  | { kind: "result"; result: FriendResult }
  | { kind: "invite"; invite: RoomInvite }
  | { kind: "inviteResult"; clientId: string; result: InviteResult };

/** Ocho caracteres del alfabeto de los codigos, con o sin guion. */
function looksLikeCode(text: string): boolean {
  return /^[2-9A-HJKMNP-TV-Z]{4}[\s-]?[2-9A-HJKMNP-TV-Z]{4}$/i.test(text.trim());
}

export function createFriendsStore(client: OnlineClient): FriendsStore {
  let current: FriendsState | null = null;
  /** Por sala: si te invitan dos veces a la misma, vale la ultima. */
  const pendingInvites = new Map<string, RoomInvite>();
  const liveInvites = (): RoomInvite[] => {
    const now = Date.now();
    for (const [roomId, inv] of pendingInvites) if (now - inv.at > INVITE_TTL_MS) pendingInvites.delete(roomId);
    return [...pendingInvites.values()].sort((a, b) => b.at - a.at);
  };
  const listeners = new Set<(state: FriendsState | null) => void>();
  const eventListeners = new Set<(event: FriendsEvent) => void>();
  const emitEvent = (event: FriendsEvent): void => {
    for (const fn of eventListeners) fn(event);
  };

  const unsub = client.onMessage((msg) => {
    if (msg.type === "friends:sync") {
      current = {
        code: msg.code,
        friends: msg.friends,
        incoming: msg.incoming,
        outgoing: msg.outgoing,
        blocked: msg.blocked ?? [],
      };
      for (const fn of listeners) fn(current);
    } else if (msg.type === "friends:notice") {
      emitEvent({ kind: msg.kind, from: msg.from });
    } else if (msg.type === "friends:result") {
      emitEvent({ kind: "result", result: msg.result });
    } else if (msg.type === "room:invited") {
      const invite: RoomInvite = {
        roomId: msg.roomId,
        roomName: msg.roomName,
        modeId: msg.modeId,
        from: msg.from,
        ...(msg.humans !== undefined ? { humans: msg.humans } : {}),
        ...(msg.capacity !== undefined ? { capacity: msg.capacity } : {}),
        at: Date.now(),
      };
      pendingInvites.set(invite.roomId, invite);
      emitEvent({ kind: "invite", invite });
      for (const fn of listeners) fn(current);
    } else if (msg.type === "room:invite:result") {
      emitEvent({ kind: "inviteResult", clientId: msg.clientId, result: msg.result });
    }
  });

  /**
   * Pregunta y espera SU respuesta. Si no llega en 5 s (sin conexion, o el
   * servidor la ha frenado por ritmo) se resuelve con null: la pantalla dice
   * "no se pudo cargar" en vez de quedarse cargando para siempre.
   */
  const ask = <T>(request: Parameters<OnlineClient["send"]>[0], pick: (msg: ServerMessage) => T | undefined): Promise<T | null> =>
    new Promise((resolve) => {
      const timer = setTimeout(() => {
        off();
        resolve(null);
      }, 5000);
      const off = client.onMessage((msg) => {
        const got = pick(msg);
        if (got === undefined) return;
        clearTimeout(timer);
        off();
        resolve(got);
      });
      client.send(request);
    });

  return {
    state: () => current,
    subscribe(fn) {
      listeners.add(fn);
      fn(current);
      return () => listeners.delete(fn);
    },
    onEvent(fn) {
      eventListeners.add(fn);
      return () => eventListeners.delete(fn);
    },
    addByCode: (code) => client.send({ type: "friends:add", code: code.trim() }),
    addById: (clientId) => client.send({ type: "friends:add", clientId }),
    addByText(text) {
      const value = text.trim();
      if (!value) return;
      client.send(
        looksLikeCode(value)
          ? { type: "friends:add", code: value, nickname: value }
          : { type: "friends:add", nickname: value },
      );
    },
    block: (clientId) => client.send({ type: "friends:block", clientId }),
    unblock: (clientId) => client.send({ type: "friends:unblock", clientId }),
    isBlocked: (clientId) => current?.blocked.some((b) => b.clientId === clientId) ?? false,
    respond: (clientId, accept) => client.send({ type: "friends:respond", clientId, accept }),
    remove: (clientId) => client.send({ type: "friends:remove", clientId }),
    invite: (roomId, clientId) => client.send({ type: "room:invite", roomId, clientId }),
    invites: liveInvites,
    declineInvite(roomId) {
      const inv = pendingInvites.get(roomId);
      if (!inv) return;
      pendingInvites.delete(roomId);
      client.send({ type: "room:invite:decline", roomId, from: inv.from.clientId });
      for (const fn of listeners) fn(current);
    },
    dropInvite(roomId) {
      if (!pendingInvites.delete(roomId)) return;
      for (const fn of listeners) fn(current);
    },
    addViaLink: (code, roomId) => client.send({ type: "friends:add", code: code.trim(), viaRoom: roomId }),
    refresh: () => client.send({ type: "friends:get" }),
    onlineCount: () => current?.friends.filter((f) => f.status !== "offline").length ?? 0,
    isFriend: (clientId) => current?.friends.some((f) => f.clientId === clientId) ?? false,
    profileOf: (clientId) =>
      ask(
        { type: "friends:profile", clientId },
        (msg) => (msg.type === "friends:profile" && msg.clientId === clientId ? { nickname: msg.nickname, profile: msg.profile } : undefined),
      ),
    board: () => ask({ type: "friends:board" }, (msg) => (msg.type === "friends:board" ? msg.rows : undefined)),
    dispose() {
      unsub();
      listeners.clear();
      eventListeners.clear();
    },
  };
}
